import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import client from "@/lib/api";
import { AlertTriangle, LifeBuoy, Copy } from "lucide-react";

// EXPANSION 3 intake aids (additive): fallback call types so no caller is ever
// denied service, escalation alerts (recommend — dispatcher approves), and
// duplicate-call detection (advisory only — never auto-merged).

export const FallbackRow = ({ onPick, currentCode }) => {
  const [types, setTypes] = useState([]);
  useEffect(() => { client.get("/call-types-fallbacks").then((r) => setTypes(r.data)).catch(() => {}); }, []);
  if (!types.length) return null;
  return (
    <div data-testid="fallback-row" className="mt-2">
      <div className="font-mono text-[9px] text-[#6B7280] uppercase mb-1 flex items-center gap-1">
        <LifeBuoy size={10} /> No exact type? Never deny service — enter now, reclassify later (audited)
      </div>
      <div className="flex flex-wrap gap-1">
        {types.map((t) => (
          <button key={t.code} type="button" data-testid={`fallback-${t.code}`} onClick={() => onPick(t.code)}
            className={`px-2 py-1 text-[10px] font-mono border rounded-sm transition-colors ${currentCode === t.code ? "border-[#F59E0B] bg-[#F59E0B]/15 text-[#F59E0B]" : "border-[#2C2D35] text-[#9CA3AF] hover:border-[#F59E0B] hover:text-[#F59E0B]"}`}>
            {t.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export const EscalationBanner = ({ matched, recommended, currentPriority, onApply }) => {
  if (!matched.length || recommended >= currentPriority) return null;
  const reasons = [...new Set(matched.map((m) => m.reason))].slice(0, 3).join(" · ");
  return (
    <div data-testid="escalation-banner" className="mt-2 border-2 border-[#F59E0B] bg-[#F59E0B]/10 px-3 py-2 rounded-sm">
      <div className="flex items-center gap-2">
        <AlertTriangle size={15} className="text-[#F59E0B] shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-head text-xs font-bold text-[#F59E0B]">POSSIBLE ESCALATION — RECOMMEND P{recommended}</div>
          <div className="font-mono text-[10px] text-[#E5E7EB]">{reasons}</div>
          <div className="font-mono text-[9px] text-[#9CA3AF]">System recommendation only — dispatcher approval required. Change is written to the CAD narrative.</div>
        </div>
        <button type="button" data-testid="escalation-apply-btn" onClick={() => onApply(recommended, reasons)}
          className="shrink-0 px-3 py-1.5 text-xs font-head bg-[#F59E0B] text-black hover:bg-[#d97706]">
          Apply P{recommended}
        </button>
      </div>
    </div>
  );
};

export const DuplicateWarning = ({ matches }) => {
  if (!matches.length) return null;
  return (
    <div data-testid="duplicate-warning" className="mt-2 border border-[#22D3EE]/60 bg-[#22D3EE]/[0.07] px-3 py-2 rounded-sm">
      <div className="flex items-center gap-1.5 font-head text-xs font-bold text-[#22D3EE] mb-1">
        <Copy size={13} /> POSSIBLE DUPLICATE CALL — {matches.length} recent incident(s) near this location
      </div>
      {matches.map((m) => (
        <div key={m.cc_number} data-testid={`dup-${m.cc_number}`} className="flex items-center gap-2 font-mono text-[11px] text-[#E5E7EB] py-0.5">
          <span className="text-[#22D3EE] font-bold">{m.cc_number}</span>
          <span>{m.call_type_label}</span>
          <span className="text-[#9CA3AF]">{m.location?.address}</span>
          <span className="text-[#9CA3AF] uppercase">{m.status}</span>
          {m.id && <Link to={`/incident/${m.id}`} className="ml-auto text-[#3B82F6] hover:underline">view</Link>}
        </div>
      ))}
      <div className="font-mono text-[9px] text-[#9CA3AF] mt-1">Advisory only — you may link this caller to an existing incident or create a separate one. Calls are never merged automatically.</div>
    </div>
  );
};
